import { MutationConfig, queryCache, useMutation } from 'react-query';

import { SchemaAddDeclarationResponse } from '../api-schema';
import { ElectionType, IError } from '../api-types';
import { fetcher } from '../fetcher';
import { handleQueryError } from '../query-error';
import { userQueryKey } from './useUser';

type DeclarationVariables = { partyId: string; electionType: ElectionType };

function delay(ms: number) {
  if (process.env.NODE_ENV === 'test') return Promise.resolve();
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function useAddDeclaration(
  options?: MutationConfig<
    SchemaAddDeclarationResponse,
    IError,
    DeclarationVariables
  >,
) {
  return useMutation<
    SchemaAddDeclarationResponse,
    IError,
    DeclarationVariables
  >(
    async ({ partyId, electionType }) => {
      const [result] = await Promise.all([
        fetcher<SchemaAddDeclarationResponse>('/api/v1/declarations/add', {
          method: 'POST',
          body: JSON.stringify({ partyId, electionType }),
        }),
        // Ensure this request takes at least 1 second to complete
        delay(1000),
      ]);

      return result;
    },
    {
      ...options,
      onSuccess: (data, variables) => {
        // After posting, we update the user, so it shows the new state
        queryCache.invalidateQueries(userQueryKey, { throwOnError: false });

        if (options && options.onSuccess) {
          return options.onSuccess(data, variables);
        }
      },
      onError: (error, variables, snapshotValue) => {
        handleQueryError(error);
        // Make sure we still have a valid user
        queryCache.invalidateQueries(userQueryKey, { throwOnError: false });

        if (options && options.onError) {
          return options.onError(error, variables, snapshotValue);
        }
      },
    },
  );
}

export default useAddDeclaration;
